import { Capacitor } from '@capacitor/core';
import { AppConfig } from '@core/domain/AppConfig.ts';
import { ok, fail, type Result } from '@core/domain/utils/Result.ts';
import type { ApkUpdateManifest, AppUpdateCheckResult } from '../../domain/appUpdateManifest.ts';
import type { AppUpdatePort } from '../../domain/ports/AppUpdatePort.ts';
import { BonoNativeMaintenance } from './capacitor/bonoNativeMaintenanceBridge.ts';
import { normalizeSha256Hex, verifyApkSha256Hex } from './apkDownloadIntegrity.ts';

export const APK_INSTALL_PERMISSION_ERROR =
  'Permita «Instalar aplicaciones desconocidas» para Bono en Ajustes del sistema antes de continuar.';

export const APK_MANIFEST_INSECURE_ERROR =
  'El manifiesto de actualización debe servirse por HTTPS.';

export const APK_DOWNLOAD_TOO_LARGE_ERROR =
  'La descarga del APK excede el tamaño máximo permitido. No se instalará la actualización.';

/** Hard cap for OTA APK downloads (defense against memory exhaustion). */
export const MAX_APK_DOWNLOAD_BYTES = 300 * 1024 * 1024;

/** Network timeouts for OTA fetches (manifest is small, APK can be large). */
export const MANIFEST_FETCH_TIMEOUT_MS = 15000;
export const APK_FETCH_TIMEOUT_MS = 180000;

/** Parses and validates remote OTA manifest JSON (HTTPS url + mandatory SHA-256). */
export function parseApkUpdateManifest(raw: unknown): ApkUpdateManifest | null {
  if (raw == null || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const versionCode = parsePositiveInt(o.versionCode);
  const versionName = typeof o.versionName === 'string' ? o.versionName.trim() : '';
  const url = typeof o.url === 'string' ? o.url.trim() : '';
  if (versionCode == null || !versionName || !isHttpsUrl(url)) return null;
  const sha256 = typeof o.sha256 === 'string' ? normalizeSha256Hex(o.sha256) : null;
  if (!sha256) return null;
  return { versionCode, versionName, url, sha256 };
}

function parsePositiveInt(v: unknown): number | null {
  const n = typeof v === 'number' ? v : Number.parseInt(String(v ?? ''), 10);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.floor(n);
}

/** True only for well-formed HTTPS URLs (rejects http and malformed values). */
function isHttpsUrl(value: string): boolean {
  if (!value) return false;
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
}

async function fetchWithTimeout(
  input: string,
  init: RequestInit,
  timeoutMs: number,
): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

/** Capacitor Android OTA: manifest fetch, version compare, download + install. */
export function createAppUpdatePort(): AppUpdatePort {
  return {
    resolveManifestUrl(): string | null {
      return AppConfig.resolveApkUpdateManifestUrl();
    },

    isNativePlatform(): boolean {
      return Capacitor.isNativePlatform();
    },

    async checkForUpdate(): Promise<Result<AppUpdateCheckResult, string>> {
      const manifestUrl = AppConfig.resolveApkUpdateManifestUrl();
      if (!manifestUrl) {
        return ok({
          manifestConfigured: false,
          updateAvailable: false,
          currentVersionCode: null,
        });
      }
      if (!isHttpsUrl(manifestUrl)) {
        return fail(APK_MANIFEST_INSECURE_ERROR);
      }
      if (!Capacitor.isNativePlatform()) {
        return ok({
          manifestConfigured: true,
          updateAvailable: false,
          currentVersionCode: null,
        });
      }

      let currentVersionCode: number | null = null;
      try {
        const { App } = await import('@capacitor/app');
        const info = await App.getInfo();
        currentVersionCode = parsePositiveInt(info.build);
      } catch {
        return fail('No se pudo leer la versión instalada del APK.');
      }

      let remote: ApkUpdateManifest;
      try {
        const res = await fetchWithTimeout(
          manifestUrl,
          { cache: 'no-store' },
          MANIFEST_FETCH_TIMEOUT_MS,
        );
        if (!res.ok) return fail(`Manifiesto de actualización respondió HTTP ${res.status}.`);
        const parsed = parseApkUpdateManifest(await res.json());
        if (!parsed) return fail('Manifiesto de actualización inválido (versionCode, versionName, url).');
        remote = parsed;
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        return fail(msg || 'No se pudo descargar el manifiesto de actualización.');
      }

      const updateAvailable =
        currentVersionCode != null && remote.versionCode > currentVersionCode;

      return ok({
        manifestConfigured: true,
        updateAvailable,
        currentVersionCode,
        remote,
      });
    },

    async getInstallPermissionStatus(): Promise<{ allowed: boolean }> {
      if (!Capacitor.isNativePlatform()) return { allowed: false };
      try {
        return await BonoNativeMaintenance.canInstallPackages();
      } catch {
        return { allowed: false };
      }
    },

    async openInstallPermissionSettings(): Promise<void> {
      if (!Capacitor.isNativePlatform()) return;
      await BonoNativeMaintenance.openInstallPermissionSettings();
    },

    async downloadAndInstall(
      manifest: ApkUpdateManifest,
      onProgress?: (loaded: number, total: number | null) => void,
    ): Promise<Result<void, string>> {
      if (!Capacitor.isNativePlatform()) {
        return fail('La instalación OTA solo está disponible en la aplicación Android.');
      }

      if (!isHttpsUrl(manifest.url)) {
        return fail(APK_MANIFEST_INSECURE_ERROR);
      }

      const allowed = await BonoNativeMaintenance.canInstallPackages().catch(() => ({ allowed: false }));
      if (!allowed.allowed) {
        return fail(APK_INSTALL_PERMISSION_ERROR);
      }

      try {
        const res = await fetchWithTimeout(manifest.url, { cache: 'no-store' }, APK_FETCH_TIMEOUT_MS);
        if (!res.ok) return fail(`Descarga del APK falló (HTTP ${res.status}).`);
        const total = res.headers.get('Content-Length');
        const totalBytes = total ? Number.parseInt(total, 10) : null;
        if (totalBytes != null && Number.isFinite(totalBytes) && totalBytes > MAX_APK_DOWNLOAD_BYTES) {
          return fail(APK_DOWNLOAD_TOO_LARGE_ERROR);
        }
        const buf = await readResponseWithProgress(res, onProgress, totalBytes, MAX_APK_DOWNLOAD_BYTES);
        const okHash = await verifyApkSha256Hex(buf, manifest.sha256);
        if (!okHash) {
          return fail(
            'La integridad del APK no coincide con el SHA-256 del manifiesto. No se instalará la actualización.',
          );
        }
        await BonoNativeMaintenance.installApk({
          base64: uint8ToBase64(buf),
          filename: `bono-update-${manifest.versionCode}.apk`,
        });
        return ok(undefined);
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        return fail(msg || 'No se pudo descargar ni instalar la actualización.');
      }
    },
  };
}

async function readResponseWithProgress(
  res: Response,
  onProgress: ((loaded: number, total: number | null) => void) | undefined,
  totalBytes: number | null,
  maxBytes: number,
): Promise<Uint8Array> {
  if (!res.body || !onProgress) {
    const bytes = new Uint8Array(await res.arrayBuffer());
    if (bytes.byteLength > maxBytes) throw new Error(APK_DOWNLOAD_TOO_LARGE_ERROR);
    return bytes;
  }
  const reader = res.body.getReader();
  const chunks: Uint8Array[] = [];
  let loaded = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    if (value) {
      loaded += value.byteLength;
      if (loaded > maxBytes) {
        await reader.cancel();
        throw new Error(APK_DOWNLOAD_TOO_LARGE_ERROR);
      }
      chunks.push(value);
      onProgress(loaded, Number.isFinite(totalBytes ?? NaN) ? totalBytes : null);
    }
  }
  const out = new Uint8Array(loaded);
  let offset = 0;
  for (const c of chunks) {
    out.set(c, offset);
    offset += c.byteLength;
  }
  return out;
}

function uint8ToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}
